import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Camera,
  ChevronDown,
  Crop,
  Eye,
  Fence,
  ImageIcon,
  Leaf,
  MessageSquareText,
  SlidersHorizontal,
  Sparkles,
  Square,
  RectangleHorizontal,
  RectangleVertical,
  Upload,
  X,
} from 'lucide-react';
import { collection, deleteField, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import DesignAreaModalActions from '../components/DesignAreaModalActions';
import StyledRangeSlider from '../components/StyledRangeSlider';
import { useDesignAreaCropSnapshot } from '../hooks/useDesignAreaCropSnapshot';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useIsInsideAppLayout } from '../components/AppLayoutContext';
import VerticalAiBeforeAfterSlider from '../components/VerticalAiBeforeAfterSlider';
import NewDesignSelect from '../components/NewDesignSelect';
import Seo from '../seo/Seo';
import { buildPublicToolPageSchemas } from '../seo/structuredData';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import { db, storage } from '../firebase';
import DesignOutputWorkspace from '../components/designWorkspace/DesignOutputWorkspace';
import DesignResultViewer from '../components/designWorkspace/DesignResultViewer';
import ToolSeoSections from '../components/ToolSeoSections';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';
import { useDesignWorkspaceMode } from '../hooks/useDesignWorkspaceMode';
import { API_BASE_URL, HAS_API_URL } from '../config/api';
import { fetchProjectDoc } from '../lib/fetchProjectDoc';
import { fetchProjectOriginalBlob } from '../lib/fetchProjectOriginalImage';
import { requestGenerateDesign } from '../lib/requestGenerateDesign';
import { downloadUrlAsFile } from '../lib/downloadUrlAsFile';
import { resolveEditSourcePreviewUrl } from '../lib/projectWorkspaceUtils';
import { trackEvent } from '../utils/analytics';
import { exportCroppedRoomJpeg } from '../lib/exportRoomCrop';
import {
  CROP_MODES,
  CROP_MODE_KEYS,
  defaultCropModeFromDimensions,
} from '../lib/roomCropModes';
import {
  formatGardenSelectionSummary,
  GARDEN_CHANGE_LEVEL_DEFAULT,
  GARDEN_CHANGE_LEVELS,
  GARDEN_MAN_MADE_STRUCTURES_SET,
  GARDEN_NATURAL_ELEMENTS_SET,
  GARDEN_STYLE_DEFAULT,
  GARDEN_STYLES,
  sanitizeGardenStringList,
} from '../lib/gardenDesignOptions';
import { GARDEN_DESIGN_STYLES } from '../data/gardenDesignStyles';
import { GARDEN_NATURAL_ELEMENT_OPTIONS } from '../data/gardenNaturalElements';
import { GARDEN_MAN_MADE_STRUCTURE_OPTIONS } from '../data/gardenManMadeStructures';
import { before2, after2 } from '../assets/home_page_assets';

const CROP_MODE_ICONS = {
  square: Square,
  landscape: RectangleHorizontal,
  portrait: RectangleVertical,
};

const WORKSPACE_PREVIEW = {
  alt: 'Garden design preview',
  hint: 'Explore planting, paths, seating, and outdoor style ideas for your real garden space.',
  beforeImage: before2,
  afterImage: after2,
};

const NOTES_MAX = 250;
const GENERATED_IMAGE_QUALITY_STANDARD = 'standard';
const GENERATED_IMAGE_QUALITY_HIGH = 'high';
const DEFAULT_GENERATED_IMAGE_QUALITY = GENERATED_IMAGE_QUALITY_STANDARD;
const GENERATED_IMAGE_QUALITY_OPTIONS = [
  { value: GENERATED_IMAGE_QUALITY_STANDARD, label: 'Standard image quality', cost: 1 },
  { value: GENERATED_IMAGE_QUALITY_HIGH, label: 'High image quality', cost: 3 },
];

/** @returns {'square'|'landscape'|'portrait'|null} */
function coerceCropModeFromDoc(raw) {
  if (typeof raw !== 'string') return null;
  const key = raw.trim();
  return CROP_MODE_KEYS.includes(key) ? key : null;
}

function GenerateWaitingIndicator({ submitPhase }) {
  return (
    <div className="generate-waiting-indicator" role="status" aria-live="polite">
      <div className="generate-waiting-text">
        {submitPhase === 'generate'
          ? 'Generating your garden design… please wait'
          : 'Preparing generation…'}
      </div>
      <div className="generate-progress-track" aria-hidden="true">
        <div className="generate-progress-fill" />
      </div>
    </div>
  );
}

function GardenDesignPage({ publicSeo = null }) {
  const isInsideAppLayout = useIsInsideAppLayout();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cropPixelsRef = useRef(null);
  const croppedPreviewGenRef = useRef(0);
  const initialCropModeOverrideRef = useRef(null);
  const editHydrationProjectIdRef = useRef(null);
  const { user } = useAuth();
  const credits = useCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editFromUrl = useMemo(() => {
    const raw = searchParams.get('edit');
    return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
  }, [searchParams]);

  const {
    pageMode,
    resultViewMode,
    setResultViewMode,
    currentProject,
    setCurrentProject,
    enterEditMode,
    enterOutputMode,
    resetToDesign,
    isEditMode,
  } = useDesignWorkspaceMode();
  const [hydratedEditId, setHydratedEditId] = useState(null);
  const [editHydration, setEditHydration] = useState(
    () => (searchParams.get('edit')?.trim() ? 'loading' : 'idle'),
  );
  const [downloadError, setDownloadError] = useState('');
  const [zoomedImage, setZoomedImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState('');
  const [, setCreditsError] = useState(false);

  const [gardenStyle, setGardenStyle] = useState(GARDEN_STYLE_DEFAULT);
  const [gardenChangeLevel, setGardenChangeLevel] = useState(() => {
    const param = searchParams.get('gardenChange');
    return GARDEN_CHANGE_LEVELS.includes(param) ? param : GARDEN_CHANGE_LEVEL_DEFAULT;
  });
  const [naturalElements, setNaturalElements] = useState([]);
  const [manMadeStructures, setManMadeStructures] = useState([]);
  const [notes, setNotes] = useState('');
  const [generatedImageQuality, setGeneratedImageQuality] = useState(DEFAULT_GENERATED_IMAGE_QUALITY);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState('');
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const [cropMode, setCropMode] = useState('square');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [hasValidCrop, setHasValidCrop] = useState(false);
  const [cropPopupOpen, setCropPopupOpen] = useState(false);
  const cropPopupOpenRef = useRef(cropPopupOpen);
  cropPopupOpenRef.current = cropPopupOpen;
  const [error, setError] = useState('');

  const [gardenStylePopupOpen, setGardenStylePopupOpen] = useState(false);
  const [naturalElementsPopupOpen, setNaturalElementsPopupOpen] = useState(false);
  const [manMadeStructuresPopupOpen, setManMadeStructuresPopupOpen] = useState(false);
  const [gardenStyleDraft, setGardenStyleDraft] = useState(GARDEN_STYLE_DEFAULT);
  const [naturalElementsDraft, setNaturalElementsDraft] = useState([]);
  const [manMadeStructuresDraft, setManMadeStructuresDraft] = useState([]);

  const gardenStyleRef = useRef(gardenStyle);
  const gardenChangeLevelRef = useRef(gardenChangeLevel);
  gardenStyleRef.current = gardenStyle;
  gardenChangeLevelRef.current = gardenChangeLevel;

  const cropConfig = CROP_MODES[cropMode] || CROP_MODES.square;
  const generatedImageCreditCost =
    generatedImageQuality === GENERATED_IMAGE_QUALITY_HIGH ? 3 : 1;
  const hasInsufficientCredits =
    credits !== null && credits < generatedImageCreditCost;
  const showOutputResultViewer = pageMode === 'output' && currentProject != null;
  const editSourcePreviewUrl = resolveEditSourcePreviewUrl({
    croppedPreviewUrl,
    previewUrl,
    project: currentProject,
  });
  const showWorkspaceResultPanel = Boolean(
    (showOutputResultViewer || isEditMode) && currentProject,
  );
  const showWorkspaceToolGuide =
    !showWorkspaceResultPanel && !editSourcePreviewUrl && !submitting;

  const naturalElementsSummary = useMemo(
    () => formatGardenSelectionSummary(naturalElements),
    [naturalElements],
  );
  const manMadeStructuresSummary = useMemo(
    () => formatGardenSelectionSummary(manMadeStructures),
    [manMadeStructures],
  );

  const resolveGeneratedProject = useCallback(async (projectId, gen, draftExtras) => {
    const fromDb = await fetchProjectDoc(projectId);
    if (fromDb) return fromDb;
    if (gen.project && typeof gen.project === 'object') {
      return { id: projectId, ...gen.project };
    }
    return {
      id: projectId,
      generatedImageUrl: gen.generatedImageUrl || '',
      status: 'completed',
      toolType: 'garden_design',
      designTool: 'garden-design',
      ...draftExtras,
    };
  }, []);

  const handleOutputEdit = useCallback(() => {
    if (currentProject?.id) setHydratedEditId(String(currentProject.id));
    enterEditMode();
  }, [currentProject, enterEditMode]);

  const openGardenStylePopup = useCallback(() => {
    setNaturalElementsPopupOpen(false);
    setManMadeStructuresPopupOpen(false);
    setGardenStyleDraft(gardenStyle);
    setGardenStylePopupOpen(true);
  }, [gardenStyle]);

  const closeGardenStylePopup = useCallback(() => {
    setGardenStylePopupOpen(false);
  }, []);

  const handleGardenStyleCancel = useCallback(() => {
    closeGardenStylePopup();
  }, [closeGardenStylePopup]);

  const handleGardenStyleConfirm = useCallback(() => {
    if (GARDEN_STYLES.includes(gardenStyleDraft)) {
      setGardenStyle(gardenStyleDraft);
    }
    closeGardenStylePopup();
  }, [gardenStyleDraft, closeGardenStylePopup]);

  const openNaturalElementsPopup = useCallback(() => {
    setGardenStylePopupOpen(false);
    setManMadeStructuresPopupOpen(false);
    setNaturalElementsDraft([...naturalElements]);
    setNaturalElementsPopupOpen(true);
  }, [naturalElements]);

  const closeNaturalElementsPopup = useCallback(() => {
    setNaturalElementsPopupOpen(false);
  }, []);

  const handleNaturalElementsCancel = useCallback(() => {
    closeNaturalElementsPopup();
  }, [closeNaturalElementsPopup]);

  const handleNaturalElementsConfirm = useCallback(() => {
    setNaturalElements(
      sanitizeGardenStringList(naturalElementsDraft, GARDEN_NATURAL_ELEMENTS_SET),
    );
    closeNaturalElementsPopup();
  }, [naturalElementsDraft, closeNaturalElementsPopup]);

  const toggleNaturalElementDraft = useCallback((label) => {
    setNaturalElementsDraft((prev) => {
      if (prev.includes(label)) {
        return prev.filter((item) => item !== label);
      }
      return [...prev, label];
    });
  }, []);

  const openManMadeStructuresPopup = useCallback(() => {
    setGardenStylePopupOpen(false);
    setNaturalElementsPopupOpen(false);
    setManMadeStructuresDraft([...manMadeStructures]);
    setManMadeStructuresPopupOpen(true);
  }, [manMadeStructures]);

  const closeManMadeStructuresPopup = useCallback(() => {
    setManMadeStructuresPopupOpen(false);
  }, []);

  const handleManMadeStructuresCancel = useCallback(() => {
    closeManMadeStructuresPopup();
  }, [closeManMadeStructuresPopup]);

  const handleManMadeStructuresConfirm = useCallback(() => {
    setManMadeStructures(
      sanitizeGardenStringList(manMadeStructuresDraft, GARDEN_MAN_MADE_STRUCTURES_SET),
    );
    closeManMadeStructuresPopup();
  }, [manMadeStructuresDraft, closeManMadeStructuresPopup]);

  const toggleManMadeStructureDraft = useCallback((label) => {
    setManMadeStructuresDraft((prev) => {
      if (prev.includes(label)) {
        return prev.filter((item) => item !== label);
      }
      return [...prev, label];
    });
  }, []);

  useEffect(() => {
    const popupOpen =
      gardenStylePopupOpen || naturalElementsPopupOpen || manMadeStructuresPopupOpen;
    if (!popupOpen || submitting) return undefined;
    function handleEsc(event) {
      if (event.key !== 'Escape') return;
      if (gardenStylePopupOpen) handleGardenStyleCancel();
      else if (naturalElementsPopupOpen) handleNaturalElementsCancel();
      else if (manMadeStructuresPopupOpen) handleManMadeStructuresCancel();
    }
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [
    gardenStylePopupOpen,
    naturalElementsPopupOpen,
    manMadeStructuresPopupOpen,
    submitting,
    handleGardenStyleCancel,
    handleNaturalElementsCancel,
    handleManMadeStructuresCancel,
  ]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
    };
  }, [croppedPreviewUrl]);

  const refreshCroppedPreview = useCallback(async () => {
    const pixels = cropPixelsRef.current;
    if (!previewUrl || !pixels?.width || !pixels?.height) {
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return;
    }
    const gen = ++croppedPreviewGenRef.current;
    try {
      const cfg = CROP_MODES[cropMode] || CROP_MODES.square;
      const blob = await exportCroppedRoomJpeg(
        previewUrl,
        pixels,
        cfg.outWidth,
        cfg.outHeight,
        0.82,
      );
      if (gen !== croppedPreviewGenRef.current) return;
      const url = URL.createObjectURL(blob);
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      if (gen !== croppedPreviewGenRef.current) return;
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  }, [previewUrl, cropMode]);

  const handleCropComplete = useCallback(
    (_area, areaPixels) => {
      cropPixelsRef.current = areaPixels;
      const valid = Boolean(areaPixels?.width > 0 && areaPixels?.height > 0);
      setHasValidCrop(valid);
      if (valid) {
        void refreshCroppedPreview();
      } else {
        setCroppedPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return '';
        });
      }
    },
    [refreshCroppedPreview],
  );

  const {
    openCropModal,
    setCropModalSnapshot,
    cancelCropModal,
    clearSnapshot,
  } = useDesignAreaCropSnapshot({
    cropMode,
    crop,
    zoom,
    hasValidCrop,
    croppedPreviewUrl,
    cropPixelsRef,
    setCropMode,
    setCrop,
    setZoom,
    setHasValidCrop,
    setCroppedPreviewUrl,
    setCropPopupOpen,
  });

  const handleCropDone = useCallback(() => {
    clearSnapshot();
    setCropPopupOpen(false);
  }, [clearSnapshot]);

  const handleDimensionLoad = useCallback((event) => {
    const img = event.currentTarget;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setNaturalWidth(w);
    setNaturalHeight(h);
    const overrideMode = initialCropModeOverrideRef.current;
    initialCropModeOverrideRef.current = null;
    const nextMode =
      overrideMode &&
      CROP_MODES[overrideMode] &&
      CROP_MODE_KEYS.includes(overrideMode)
        ? overrideMode
        : defaultCropModeFromDimensions(w, h);
    setCropMode(nextMode);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    if (cropPopupOpenRef.current) {
      setCropModalSnapshot({
        cropMode: nextMode,
        crop: { x: 0, y: 0 },
        zoom: 1,
        cropPixels: null,
        hasValidCrop: false,
        croppedPreviewUrl: '',
      });
    }
  }, [setCropModalSnapshot]);

  const handleCropModeChange = useCallback((mode) => {
    setCropMode(mode);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  const applyUploadedImageFile = useCallback((nextFile, { openCropPopup = true } = {}) => {
    if (!nextFile || !nextFile.type.startsWith('image/')) return;
    setError('');
    setNaturalWidth(0);
    setNaturalHeight(0);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(nextFile);
    });
    setFile(nextFile);
    if (openCropPopup) {
      setCropPopupOpen(true);
    }
  }, []);

  const handleFileChange = useCallback(
    (nextFile) => {
      applyUploadedImageFile(nextFile, { openCropPopup: true });
      trackEvent('garden_image_uploaded', {
        design_tool: 'garden-design',
        design_style: gardenStyleRef.current,
        garden_change_level: gardenChangeLevelRef.current,
      });
    },
    [applyUploadedImageFile],
  );

  const handleInputChange = useCallback(
    (event) => {
      const nextFile = event.target.files?.[0];
      if (nextFile) handleFileChange(nextFile);
      event.target.value = '';
    },
    [handleFileChange],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const nextFile = event.dataTransfer.files?.[0];
      if (nextFile) handleFileChange(nextFile);
    },
    [handleFileChange],
  );

  const handleNotesChange = useCallback((e) => {
    setNotes(e.target.value.slice(0, NOTES_MAX));
  }, []);

  useEffect(() => {
    if (!editFromUrl) {
      editHydrationProjectIdRef.current = null;
      setHydratedEditId(null);
      setEditHydration('idle');
      initialCropModeOverrideRef.current = null;
      return undefined;
    }
    if (editHydrationProjectIdRef.current === editFromUrl) {
      return undefined;
    }
    if (!user?.uid) return undefined;
    let cancelled = false;

    async function hydrateEditProject() {
      setHydratedEditId(null);
      setEditHydration('loading');
      try {
        const snap = await getDoc(doc(db, 'projects', editFromUrl));
        if (cancelled) return;
        if (!snap.exists()) {
          navigate('/projects', { replace: true });
          return;
        }
        const data = snap.data();
        if (data.userId !== user.uid) {
          navigate('/projects', { replace: true });
          return;
        }
        const originalUrl =
          typeof data.originalImageUrl === 'string' ? data.originalImageUrl.trim() : '';
        if (!originalUrl) {
          navigate('/projects', { replace: true });
          return;
        }

        const projectOriginalApiPath = `/api/projects/${encodeURIComponent(editFromUrl)}/original`;
        const projectOriginalApiUrl =
          HAS_API_URL && API_BASE_URL
            ? process.env.NODE_ENV === 'development'
              ? projectOriginalApiPath
              : `${API_BASE_URL}${projectOriginalApiPath}`
            : null;

        const blob = await fetchProjectOriginalBlob({
          user,
          projectId: editFromUrl,
          projectData: data,
          fallbackUrl: originalUrl,
          projectOriginalApiUrl,
        });

        if (cancelled) return;
        if (!blob.size) throw new Error('Project image was empty.');
        const type =
          blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
        const ext = type.includes('png') ? '.png' : '.jpg';
        const imageFile = new File([blob], `garden${ext}`, { type });

        setGardenStyle(
          GARDEN_STYLES.includes(data.style) ? data.style : GARDEN_STYLE_DEFAULT,
        );
        setGardenChangeLevel(
          GARDEN_CHANGE_LEVELS.includes(data.gardenChangeLevel)
            ? data.gardenChangeLevel
            : GARDEN_CHANGE_LEVEL_DEFAULT,
        );
        setNaturalElements(
          sanitizeGardenStringList(data.naturalElements, GARDEN_NATURAL_ELEMENTS_SET),
        );
        setManMadeStructures(
          sanitizeGardenStringList(data.manMadeStructures, GARDEN_MAN_MADE_STRUCTURES_SET),
        );
        setNotes(typeof data.notes === 'string' ? data.notes.trim().slice(0, NOTES_MAX) : '');

        const savedQuality =
          typeof data.imageQuality === 'string' ? data.imageQuality.trim().toLowerCase() : '';
        if (savedQuality === GENERATED_IMAGE_QUALITY_HIGH) {
          setGeneratedImageQuality(GENERATED_IMAGE_QUALITY_HIGH);
        } else if (savedQuality === GENERATED_IMAGE_QUALITY_STANDARD) {
          setGeneratedImageQuality(GENERATED_IMAGE_QUALITY_STANDARD);
        }

        const savedCrop = coerceCropModeFromDoc(data.cropMode);
        initialCropModeOverrideRef.current = savedCrop;

        if (data.generatedImageUrl) {
          setCurrentProject({ id: snap.id, ...data });
        }

        applyUploadedImageFile(imageFile, { openCropPopup: true });

        editHydrationProjectIdRef.current = editFromUrl;
        setHydratedEditId(editFromUrl);
        setEditHydration('ready');
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Could not load this project for editing.');
          setEditHydration('idle');
          navigate('/projects', { replace: true });
        }
      }
    }

    void hydrateEditProject();
    return () => {
      cancelled = true;
    };
  }, [editFromUrl, user, navigate, setCurrentProject, applyUploadedImageFile]);

  const handleSave = async () => {
    if (!user?.uid) {
      navigate(`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
      return;
    }
    if (!file) {
      setError('Please choose a garden or outdoor photo first.');
      return;
    }
    if (!GARDEN_STYLES.includes(gardenStyle)) {
      setError('Please choose a garden style.');
      return;
    }
    const pixels = cropPixelsRef.current;
    if (!pixels || !previewUrl || !hasValidCrop) {
      setError('Adjust the crop under your uploaded photo, then try again.');
      return;
    }
    if (submitting) return;

    setError('');
    setCreditsError(false);
    setSubmitting(true);
    setSubmitPhase('upload');

    trackEvent('design_generation_started', {
      design_tool: 'garden-design',
      design_style: gardenStyle,
      garden_change_level: gardenChangeLevel,
      image_quality: generatedImageQuality,
    });

    try {
      const cfg = CROP_MODES[cropMode] || CROP_MODES.square;
      const croppedBlob = await exportCroppedRoomJpeg(
        previewUrl,
        pixels,
        cfg.outWidth,
        cfg.outHeight,
      );
      const croppedFile = new File([croppedBlob], 'garden.jpg', {
        type: 'image/jpeg',
      });

      if (!HAS_API_URL) {
        setError(
          'The design server is not configured. Set REACT_APP_API_URL in your environment to generate and save projects.',
        );
        return;
      }

      const projectId = doc(collection(db, 'projects')).id;
      const storagePathNew = `users/${user.uid}/projects/${projectId}/original`;
      const storageRefNew = ref(storage, storagePathNew);

      await uploadBytes(storageRefNew, croppedFile);
      const originalImageUrlNew = await getDownloadURL(storageRefNew);

      const naturalElementsForDoc = sanitizeGardenStringList(
        naturalElements,
        GARDEN_NATURAL_ELEMENTS_SET,
      );
      const manMadeStructuresForDoc = sanitizeGardenStringList(
        manMadeStructures,
        GARDEN_MAN_MADE_STRUCTURES_SET,
      );

      if (hydratedEditId) {
        const projectRef = doc(db, 'projects', hydratedEditId);
        const storagePath = `users/${user.uid}/projects/${hydratedEditId}/original`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, croppedFile);
        const originalImageUrl = await getDownloadURL(storageRef);
        await updateDoc(projectRef, {
          style: gardenStyle,
          gardenChangeLevel,
          naturalElements: naturalElementsForDoc,
          manMadeStructures: manMadeStructuresForDoc,
          notes: notes.trim().slice(0, NOTES_MAX),
          originalImageUrl,
          cropMode,
          inputOpenAiSize: cfg.openAiSize,
          generatedImageUrl: '',
          status: 'uploaded',
          errorMessage: deleteField(),
        });
        setSubmitPhase('generate');
        const gen = await requestGenerateDesign(user, hydratedEditId, {
          regenerate: true,
          imageQuality: generatedImageQuality,
        });
        if (!gen.ok) {
          if (gen.code === 'INSUFFICIENT_CREDITS') setCreditsError(true);
          else setError(gen.error);
          return;
        }
        const regenProject = await resolveGeneratedProject(hydratedEditId, gen, {
          style: gardenStyle,
          gardenChangeLevel,
          naturalElements: naturalElementsForDoc,
          manMadeStructures: manMadeStructuresForDoc,
          originalImageUrl,
        });
        enterOutputMode(regenProject);
        return;
      }

      const projectDraft = {
        designTool: 'garden-design',
        toolType: 'garden_design',
        style: gardenStyle,
        gardenChangeLevel,
        naturalElements: naturalElementsForDoc,
        manMadeStructures: manMadeStructuresForDoc,
        notes: notes.trim().slice(0, NOTES_MAX),
        originalImageUrl: originalImageUrlNew,
        cropMode,
        inputOpenAiSize: cfg.openAiSize,
        ...(naturalWidth ? { originalImageWidth: naturalWidth } : {}),
        ...(naturalHeight ? { originalImageHeight: naturalHeight } : {}),
      };

      setSubmitPhase('generate');
      const gen = await requestGenerateDesign(user, projectId, {
        imageQuality: generatedImageQuality,
        pendingCreate: true,
        projectDraft,
      });

      if (!gen.ok) {
        if (gen.code === 'INSUFFICIENT_CREDITS') {
          setCreditsError(true);
        } else {
          setError(gen.error);
        }
        return;
      }

      trackEvent('design_generation_completed', {
        design_tool: 'garden-design',
        design_style: gardenStyle,
        garden_change_level: gardenChangeLevel,
        image_quality: generatedImageQuality,
        project_id: projectId,
      });

      const completedProject = await resolveGeneratedProject(projectId, gen, {
        ...projectDraft,
        originalImageUrl: originalImageUrlNew,
      });
      enterOutputMode(completedProject);
      setHydratedEditId(projectId);
    } catch (err) {
      setError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
      setSubmitPhase('');
    }
  };

  const gardenLevelOptions = useMemo(
    () => GARDEN_CHANGE_LEVELS.map((level) => ({ value: level, label: level })),
    [],
  );

  const editRouteBlocking = Boolean(editFromUrl) && editHydration !== 'ready';

  return (
    <>
      <Seo
        title={publicSeo?.title || 'AI Garden Design | MyRoomsAI'}
        description={
          publicSeo?.description ||
          'Upload a garden or outdoor photo and generate a beautiful landscape design with AI.'
        }
        canonicalPath={publicSeo?.path || '/projects/ai-garden-design'}
        noindex={!publicSeo}
        structuredData={
          publicSeo
            ? [...buildPublicToolPageSchemas(publicSeo), getSeoFaqSchema(publicSeo.path)].filter(Boolean)
            : []
        }
      />
      {!isInsideAppLayout ? <Navbar /> : null}
      <main
        className={`dashboard-page new-design-page garden-design-page${
          pageMode === 'output' ? ' project-detail-page' : ''
        }`}
      >
        <div className="container">
          {error ? (
            <p className="dashboard-inline-error" role="alert">
              {error}
            </p>
          ) : null}

          {editHydration === 'loading' ? (
            <p className="project-page-muted" role="status">
              Loading project…
            </p>
          ) : null}

          {pageMode === 'output' && currentProject ? (
            <>
              <DesignOutputWorkspace
                project={currentProject}
                resultViewMode={resultViewMode}
                onResultViewModeChange={setResultViewMode}
                onNewProject={resetToDesign}
                onEdit={handleOutputEdit}
                shopItemCount={0}
                onDownloadImage={async (url, baseName) => {
                  setDownloadError('');
                  try {
                    await downloadUrlAsFile(url, baseName, user);
                  } catch (err) {
                    setDownloadError(err?.message || 'Download failed.');
                  }
                }}
                onZoomImage={setZoomedImage}
                downloadError={downloadError}
              />
            </>
          ) : (
          <section
            className={`dashboard-grid new-design-workspace design-dual-layout${
              isEditMode ? ' edit-mode' : ''
            }${!showWorkspaceResultPanel ? ' new-design-workspace--preview-heading' : ''}`}
          >
            <article className="card new-design-setup-card">
                <>
                  {!publicSeo ? (
                    <div className="new-design-setup-nav tool-category-tabs">
                      <button
                        type="button"
                        className="tool-category-tab is-active new-design-back-to-tools"
                        onClick={() => navigate('/projects')}
                        aria-label="Back to design tools"
                      >
                        <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                        Back to design tools
                      </button>
                    </div>
                  ) : null}

                  <div className="new-design-upload-mobile-summary">
                    <h3 className="new-design-subheading new-design-upload-section-heading">
                      <Upload
                        className="new-design-subheading-icon"
                        size={18}
                        strokeWidth={2}
                        aria-hidden
                      />
                      Upload Garden Photo
                    </h3>
                    <input
                      ref={fileInputRef}
                      id="garden-design-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleInputChange}
                      className="new-design-file-input"
                      disabled={editRouteBlocking || submitting}
                    />
                    <input
                      ref={cameraInputRef}
                      id="garden-design-camera"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleInputChange}
                      className="new-design-file-input"
                      disabled={editRouteBlocking || submitting}
                    />
                    {!previewUrl ? (
                      <div className="new-design-upload-empty-stack">
                        <div className="new-design-upload-dropzone-wrap">
                          <label
                            htmlFor="garden-design-upload"
                            className="upload-dropzone"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                          >
                            <div className="upload-dropzone-inner">
                              <span className="upload-dropzone-cloud" aria-hidden="true">
                                <Upload size={38} strokeWidth={1.75} />
                              </span>
                              <span>Upload your garden or outdoor photo</span>
                            </div>
                          </label>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline new-design-camera-action"
                          onClick={() => cameraInputRef.current?.click()}
                          disabled={editRouteBlocking || submitting}
                        >
                          <Camera size={18} strokeWidth={2} aria-hidden="true" />
                          Take Photo
                        </button>
                      </div>
                    ) : (
                      <>
                        <img
                          src={previewUrl}
                          alt=""
                          onLoad={handleDimensionLoad}
                          className="new-design-dims-probe"
                          decoding="async"
                          loading="lazy"
                        />
                        <div className="new-design-upload-mobile-preview">
                          <img
                            src={previewUrl}
                            alt="Uploaded garden preview"
                            className="new-design-upload-mobile-preview-img"
                            decoding="async"
                          />
                        </div>
                        <div className="new-design-upload-mobile-summary-btns">
                          <div className="new-design-upload-summary-btn-row">
                            <button
                              type="button"
                              className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={editRouteBlocking || submitting}
                            >
                              <ImageIcon size={15} strokeWidth={2} aria-hidden="true" />
                              Change Photo
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn"
                              onClick={openCropModal}
                              disabled={editRouteBlocking || submitting}
                            >
                              <Crop size={15} strokeWidth={2} aria-hidden="true" />
                              Change Design Area
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {previewUrl && cropPopupOpen ? (
                    <div
                      className="new-design-theme-popup-backdrop"
                      role="presentation"
                      onClick={cancelCropModal}
                    >
                      <div
                        className="new-design-crop-popup"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="garden-design-crop-title"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="new-design-crop-panel-head">
                          <h3 id="garden-design-crop-title" className="new-design-subheading">
                            <Crop
                              className="new-design-subheading-icon"
                              size={18}
                              strokeWidth={2}
                              aria-hidden
                            />
                            Select design area
                          </h3>
                        </div>
                        <p className="new-design-hint">
                          Zoom in/out and then drag to frame the design area.
                        </p>
                        <div className="new-design-crop-mode-row" role="group" aria-label="Crop aspect ratio">
                          {CROP_MODE_KEYS.map((key) => {
                            const CropModeIcon = CROP_MODE_ICONS[key];
                            return (
                            <button
                              key={key}
                              type="button"
                              className={`new-design-chip new-design-crop-mode-btn${
                                cropMode === key ? ' new-design-chip--selected' : ''
                              }`}
                              aria-pressed={cropMode === key}
                              aria-label={CROP_MODES[key].label}
                              title={CROP_MODES[key].label}
                              onClick={() => handleCropModeChange(key)}
                            >
                              <CropModeIcon size={17} strokeWidth={2} aria-hidden="true" />
                            </button>
                            );
                          })}
                        </div>
                        <div className="new-design-crop-stage">
                          <Cropper
                            image={previewUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={cropConfig.aspect}
                            cropShape="rect"
                            showGrid
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleCropComplete}
                          />
                        </div>
                        <div className="new-design-crop-zoom">
                          <span className="new-design-crop-zoom-label">Zoom</span>
                          <StyledRangeSlider
                            min={1}
                            max={3}
                            step={0.02}
                            value={zoom}
                            aria-label="Crop zoom"
                            onChange={(e) => setZoom(Number(e.target.value))}
                          />
                        </div>
                        <DesignAreaModalActions
                          onCancel={cancelCropModal}
                          onDone={handleCropDone}
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="new-design-setup-fields">
                    <div className="new-design-control-group">
                      <h3 className="new-design-subheading">
                        <Eye
                          className="new-design-subheading-icon"
                          size={18}
                          strokeWidth={2}
                          aria-hidden
                        />
                        Garden Style
                      </h3>
                      <div
                        className={`new-design-select${
                          gardenStylePopupOpen ? ' new-design-select--open' : ''
                        }`}
                      >
                        <button
                          type="button"
                          id="garden-design-style"
                          className="new-design-select-trigger"
                          aria-haspopup="dialog"
                          aria-expanded={gardenStylePopupOpen}
                          aria-controls="garden-design-style-popup"
                          onClick={openGardenStylePopup}
                          disabled={editRouteBlocking || submitting}
                        >
                          <span className="new-design-select-trigger-label">{gardenStyle}</span>
                          <span className="new-design-select-trigger-meta">
                            <ChevronDown
                              className="new-design-select-chevron"
                              size={14}
                              strokeWidth={2.5}
                              aria-hidden
                            />
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="new-design-control-group">
                      <h3 className="new-design-subheading">
                        <Leaf
                          className="new-design-subheading-icon"
                          size={18}
                          strokeWidth={2}
                          aria-hidden
                        />
                        Natural Elements
                      </h3>
                      <div
                        className={`new-design-select${
                          naturalElementsPopupOpen ? ' new-design-select--open' : ''
                        }`}
                      >
                        <button
                          type="button"
                          id="garden-design-natural-elements"
                          className="new-design-select-trigger"
                          aria-haspopup="dialog"
                          aria-expanded={naturalElementsPopupOpen}
                          aria-controls="garden-design-natural-elements-popup"
                          onClick={openNaturalElementsPopup}
                          disabled={editRouteBlocking || submitting}
                        >
                          <span className="new-design-select-trigger-label">
                            {naturalElementsSummary}
                          </span>
                          <span className="new-design-select-trigger-meta">
                            <ChevronDown
                              className="new-design-select-chevron"
                              size={14}
                              strokeWidth={2.5}
                              aria-hidden
                            />
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="new-design-control-group">
                      <h3 className="new-design-subheading">
                        <Fence
                          className="new-design-subheading-icon"
                          size={18}
                          strokeWidth={2}
                          aria-hidden
                        />
                        Man-Made Structures
                      </h3>
                      <div
                        className={`new-design-select${
                          manMadeStructuresPopupOpen ? ' new-design-select--open' : ''
                        }`}
                      >
                        <button
                          type="button"
                          id="garden-design-man-made-structures"
                          className="new-design-select-trigger"
                          aria-haspopup="dialog"
                          aria-expanded={manMadeStructuresPopupOpen}
                          aria-controls="garden-design-man-made-structures-popup"
                          onClick={openManMadeStructuresPopup}
                          disabled={editRouteBlocking || submitting}
                        >
                          <span className="new-design-select-trigger-label">
                            {manMadeStructuresSummary}
                          </span>
                          <span className="new-design-select-trigger-meta">
                            <ChevronDown
                              className="new-design-select-chevron"
                              size={14}
                              strokeWidth={2.5}
                              aria-hidden
                            />
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="new-design-control-group">
                      <h3 className="new-design-subheading">
                        <SlidersHorizontal
                          className="new-design-subheading-icon"
                          size={18}
                          strokeWidth={2}
                          aria-hidden
                        />
                        Garden Change Level
                      </h3>
                      <NewDesignSelect
                        id="garden-design-change-level"
                        value={gardenChangeLevel}
                        onChange={setGardenChangeLevel}
                        disabled={editRouteBlocking || submitting}
                        options={gardenLevelOptions}
                      />
                    </div>
                  </div>

                  <div className="new-design-review-notes-group">
                    <h3 className="new-design-subheading">
                      <MessageSquareText
                        className="new-design-subheading-icon"
                        size={18}
                        strokeWidth={2}
                        aria-hidden
                      />
                      Notes & instructions for the AI
                    </h3>
                    <textarea
                      className="dashboard-notes new-design-notes"
                      rows={2}
                      maxLength={NOTES_MAX}
                      placeholder="Example: Add more flowering plants along the path, keep the existing trees."
                      value={notes}
                      onChange={handleNotesChange}
                      disabled={editRouteBlocking || submitting}
                    />
                    <p className="new-design-char-count" aria-live="polite">
                      {notes.length}/{NOTES_MAX}
                    </p>
                  </div>

                  <div className="new-design-control-group new-design-setup-quality-group">
                    <h3 className="new-design-subheading" id="garden-design-quality-label">
                      <ImageIcon
                        className="new-design-subheading-icon"
                        size={18}
                        strokeWidth={2}
                        aria-hidden
                      />
                      Image quality
                    </h3>
                    <NewDesignSelect
                      id="garden-design-image-quality"
                      aria-labelledby="garden-design-quality-label"
                      value={generatedImageQuality}
                      disabled={editRouteBlocking || submitting}
                      onChange={setGeneratedImageQuality}
                      options={GENERATED_IMAGE_QUALITY_OPTIONS}
                    />
                  </div>

                  <div className="new-design-setup-generate-group">
                    <button
                      type="button"
                      className="btn btn-primary full-width new-design-generate-btn"
                      onClick={() => void handleSave()}
                      disabled={
                        editRouteBlocking ||
                        submitting ||
                        !file ||
                        !naturalWidth ||
                        !hasValidCrop ||
                        hasInsufficientCredits
                      }
                    >
                      <Sparkles size={25} strokeWidth={2} aria-hidden="true" />
                      Generate design
                    </button>
                    {hasInsufficientCredits ? (
                      <div className="new-design-no-credits" role="status">
                        <span>You do not have enough credits.</span>
                        <Link to="/credits" className="btn btn-outline new-design-get-credit-btn">
                          Get credit
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </>
            </article>

            {showWorkspaceResultPanel ? (
              <DesignResultViewer
                project={currentProject}
                editSourceImageUrl={isEditMode ? editSourcePreviewUrl : null}
                resultViewMode={resultViewMode}
                onDownloadImage={async (url, baseName) => {
                  setDownloadError('');
                  try {
                    await downloadUrlAsFile(url, baseName, user);
                  } catch (err) {
                    setDownloadError(err?.message || 'Download failed.');
                  }
                }}
                onZoomImage={setZoomedImage}
                previewLayout="plain"
                showMobileActions={false}
                showShopOnMobile={false}
              />
            ) : (
              <div
                className={`new-design-workspace-preview${
                  croppedPreviewUrl ? ' new-design-workspace-preview--has-image' : ''
                }${showWorkspaceToolGuide ? ' new-design-workspace-preview--has-guide' : ''}`}
                aria-label="Design preview"
              >
                <div className="new-design-workspace-preview-heading-group">
                  <h1 className="tool-hub-heading">
                    {publicSeo?.h1 ? (
                      publicSeo.h1
                    ) : (
                      <>
                        AI Garden <span className="new-design-page-heading-accent">Design</span>
                      </>
                    )}
                  </h1>
                  {publicSeo?.headingSubtext ? (
                    <p className="new-design-workspace-preview-heading-subtext">{publicSeo.headingSubtext}</p>
                  ) : null}
                  {showWorkspaceToolGuide ? (
                    <p className="new-design-workspace-preview-tool-hint">{WORKSPACE_PREVIEW.hint}</p>
                  ) : null}
                </div>
                {showWorkspaceToolGuide ? (
                  <div className="new-design-workspace-preview-guide">
                    <div className="new-design-workspace-preview-tool-media new-design-workspace-preview-tool-media--vertical-scan">
                      <div className="new-design-workspace-preview-offset-rectangle" aria-hidden="true" />
                      <VerticalAiBeforeAfterSlider
                        beforeImage={WORKSPACE_PREVIEW.beforeImage}
                        afterImage={WORKSPACE_PREVIEW.afterImage}
                        beforeAlt={`${WORKSPACE_PREVIEW.alt} before`}
                        afterAlt={`${WORKSPACE_PREVIEW.alt} after`}
                        fallbackSrc={WORKSPACE_PREVIEW.afterImage}
                        showLabels
                      />
                    </div>
                  </div>
                ) : null}
                {croppedPreviewUrl || submitting ? (
                  <div className="new-design-workspace-preview-content">
                    {croppedPreviewUrl ? (
                      <div className="new-design-workspace-preview-media">
                        <img
                          src={croppedPreviewUrl}
                          alt="Cropped garden preview"
                          className="new-design-workspace-preview-img"
                          decoding="async"
                        />
                      </div>
                    ) : null}
                    {submitting ? <GenerateWaitingIndicator submitPhase={submitPhase} /> : null}
                  </div>
                ) : null}
              </div>
            )}
          </section>
          )}
          {publicSeo ? <ToolSeoSections {...publicSeo} /> : null}
          {publicSeo ? <CollapsibleSeoContent {...getSeoContent(publicSeo.path)} /> : null}
        </div>
        {!isInsideAppLayout ? <Footer /> : null}
      </main>
      {zoomedImage ? (
        <div
          className="image-lightbox-backdrop"
          role="presentation"
          onClick={() => setZoomedImage(null)}
        >
          <div
            className="image-lightbox-content"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="image-lightbox-close"
              aria-label="Close image preview"
              onClick={() => setZoomedImage(null)}
            >
              Close
            </button>
            <img
              src={zoomedImage.src}
              alt={zoomedImage.alt}
              className="image-lightbox-img"
              loading="eager"
              decoding="sync"
            />
          </div>
        </div>
      ) : null}

      {gardenStylePopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={handleGardenStyleCancel}
        >
          <div
            id="garden-design-style-popup"
            className="new-design-theme-popup new-design-theme-popup--garden-picker new-design-theme-popup--garden-style"
            role="dialog"
            aria-modal="true"
            aria-labelledby="garden-design-style-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="garden-design-style-popup-title" className="new-design-theme-popup-title">
                Choose landscape style
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                aria-label="Close"
                onClick={handleGardenStyleCancel}
              >
                <X size={18} strokeWidth={2.25} aria-hidden />
              </button>
            </div>
            <div className="new-design-theme-popup-scroll-body">
              <div
                className="new-design-furniture-grid exterior-style-picker-grid garden-style-picker-grid"
                role="group"
                aria-label="Garden style options"
              >
                {GARDEN_DESIGN_STYLES.map((style) => {
                  const isSelected = gardenStyleDraft === style.label;
                  const StyleIcon = style.Icon;
                  return (
                    <button
                      key={style.id}
                      type="button"
                      className={`new-design-furniture-tile exterior-style-card-tile${
                        isSelected ? ' new-design-furniture-tile--active' : ''
                      }`}
                      aria-pressed={isSelected}
                      aria-label={style.label}
                      onClick={() => setGardenStyleDraft(style.label)}
                    >
                      {style.image ? (
                        <img
                          src={style.image}
                          alt=""
                          className="exterior-style-card-thumb"
                          loading="lazy"
                          decoding="async"
                          aria-hidden="true"
                        />
                      ) : (
                        <span
                          className="exterior-style-card-thumb exterior-style-card-thumb--placeholder"
                          aria-hidden="true"
                        >
                          {StyleIcon ? (
                            <StyleIcon size={28} strokeWidth={1.75} />
                          ) : null}
                        </span>
                      )}
                      <span className="new-design-furniture-tile-label">{style.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="new-design-theme-popup-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleGardenStyleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleGardenStyleConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {naturalElementsPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={handleNaturalElementsCancel}
        >
          <div
            id="garden-design-natural-elements-popup"
            className="new-design-theme-popup new-design-theme-popup--garden-picker new-design-theme-popup--garden-elements"
            role="dialog"
            aria-modal="true"
            aria-labelledby="garden-design-natural-elements-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2
                id="garden-design-natural-elements-popup-title"
                className="new-design-theme-popup-title"
              >
                Choose natural elements
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                aria-label="Close"
                onClick={handleNaturalElementsCancel}
              >
                <X size={18} strokeWidth={2.25} aria-hidden />
              </button>
            </div>
            <div className="new-design-theme-popup-scroll-body">
              <div
                className="new-design-furniture-grid garden-elements-grid"
                role="group"
                aria-label="Natural element options"
              >
                {GARDEN_NATURAL_ELEMENT_OPTIONS.map(({ id, label, Icon }) => {
                  const active = naturalElementsDraft.includes(label);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`new-design-furniture-tile${
                        active ? ' new-design-furniture-tile--active' : ''
                      }`}
                      aria-pressed={active}
                      onClick={() => toggleNaturalElementDraft(label)}
                      title={label}
                    >
                      <Icon size={40} color="#586174" strokeWidth={2} />
                      <span className="new-design-furniture-tile-label">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="new-design-theme-popup-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleNaturalElementsCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNaturalElementsConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {manMadeStructuresPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={handleManMadeStructuresCancel}
        >
          <div
            id="garden-design-man-made-structures-popup"
            className="new-design-theme-popup new-design-theme-popup--garden-picker new-design-theme-popup--garden-elements"
            role="dialog"
            aria-modal="true"
            aria-labelledby="garden-design-man-made-structures-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2
                id="garden-design-man-made-structures-popup-title"
                className="new-design-theme-popup-title"
              >
                Choose man-made structures
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                aria-label="Close"
                onClick={handleManMadeStructuresCancel}
              >
                <X size={18} strokeWidth={2.25} aria-hidden />
              </button>
            </div>
            <div className="new-design-theme-popup-scroll-body">
              <div
                className="new-design-furniture-grid garden-elements-grid"
                role="group"
                aria-label="Man-made structure options"
              >
                {GARDEN_MAN_MADE_STRUCTURE_OPTIONS.map(({ id, label, Icon }) => {
                  const active = manMadeStructuresDraft.includes(label);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`new-design-furniture-tile${
                        active ? ' new-design-furniture-tile--active' : ''
                      }`}
                      aria-pressed={active}
                      onClick={() => toggleManMadeStructureDraft(label)}
                      title={label}
                    >
                      <Icon size={40} color="#586174" strokeWidth={2} />
                      <span className="new-design-furniture-tile-label">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="new-design-theme-popup-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleManMadeStructuresCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleManMadeStructuresConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default GardenDesignPage;
